import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, PoundSterling, CheckCircle2, TrendingUp, LogOut, BookOpen, Pencil, Eye, EyeOff, Plus, Trash2, X, Upload, Mail, Sparkles, Hourglass, Inbox, Send } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TABS = ["users", "trials", "submissions", "content", "toolkit", "payments", "analytics"];

function LessonEditor({ lesson, onClose, onSaved }) {
  const [f, setF] = useState({
    ...lesson,
    intro: lesson.intro || "",
    teaching_content: lesson.teaching_content || "",
    key_takeaways: (lesson.key_takeaways || []).join("\n"),
    button_label: lesson.button_label || "Complete Day",
    learn_content: (lesson.learn || lesson.learn_content || [])
      .map((c) => (c && typeof c === "object" ? `${c.title} :: ${c.body}` : c))
      .join("\n"),
    interactive_json: JSON.stringify(lesson.interactive || [], null, 2),
    learn_blocks_json: JSON.stringify(lesson.learn_blocks || [], null, 2),
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const parseLearn = (raw) =>
    raw.split("\n").filter((x) => x.trim()).map((line) => {
      const idx = line.indexOf("::");
      if (idx >= 0) return { title: line.slice(0, idx).trim(), body: line.slice(idx + 2).trim() };
      return { title: "", body: line.trim() };
    });
  const save = async () => {
    let interactive;
    try {
      interactive = JSON.parse(f.interactive_json || "[]");
      if (!Array.isArray(interactive)) throw new Error("must be a list");
    } catch (err) {
      toast.error("Interactive elements must be valid JSON (a list of fields)");
      return;
    }
    let learn_blocks;
    try {
      learn_blocks = JSON.parse(f.learn_blocks_json || "[]");
      if (!Array.isArray(learn_blocks)) throw new Error("must be a list");
    } catch (err) {
      toast.error("Rich learn blocks must be valid JSON (a list of blocks)");
      return;
    }
    try {
      await api.put(`/admin/lessons/${lesson.day}`, {
        day_number: lesson.day,
        week_number: f.week || lesson.week || 1,
        week_title: f.week_title || "",
        title: f.title,
        category: f.category || "",
        intro: f.intro || "",
        teaching_content: f.teaching_content || "",
        key_takeaways: (f.key_takeaways || "").split("\n").map((x) => x.trim()).filter(Boolean),
        learn_content: parseLearn(f.learn_content),
        learn_blocks,
        action_instruction: f.action || f.action_instruction || "",
        do_it: [],
        reflection_question: f.reflect || f.reflection_question || "",
        button_label: f.button_label || "Complete Day",
        estimated_minutes: Number(f.estimated_time || f.estimated_minutes || 5),
        published: f.published !== false,
        sort_order: lesson.day,
        interactive,
      });
      toast.success("Lesson updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Could not save lesson");
    }
  };
  const I = ({ label, k, rows }) => (
    <div>
      <label className="text-xs font-semibold text-ink">{label}</label>
      {rows ? (
        <textarea data-testid={`edit-${k}`} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} rows={rows}
          className="mt-1 w-full rounded-lg border border-subtle bg-app px-3 py-2 text-sm text-ink outline-none focus:border-[var(--brand)] resize-none" />
      ) : (
        <input data-testid={`edit-${k}`} value={f[k] || ""} onChange={(e) => set(k, e.target.value)}
          className="mt-1 w-full rounded-lg border border-subtle bg-app px-3 py-2 text-sm text-ink outline-none focus:border-[var(--brand)]" />
      )}
    </div>
  );
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="lesson-editor">
      <div className="bg-app w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-app px-5 py-4 flex items-center justify-between border-b border-subtle">
          <div className="font-serif text-lg font-bold text-ink">Edit Day {lesson.day}</div>
          <button onClick={onClose} data-testid="editor-close"><X size={22} className="text-ink-muted" /></button>
        </div>
        <div className="p-5 space-y-3">
          <I label="Title" k="title" />
          <div className="grid grid-cols-2 gap-3">
            <I label="Category" k="category" />
            <I label="Estimated minutes" k="estimated_time" />
          </div>
          <I label="Week title" k="week_title" />
          <I label="Intro line (shown under the title)" k="intro" rows={2} />
          <div>
            <label className="text-xs font-semibold text-ink">Teaching content (educational lesson text)</label>
            <p className="text-[11px] text-ink-muted mb-1">Shown at the top of the Learn section. Wrap a line in **double asterisks** for a subheading, start a line with "- " for a bullet. Leave a blank line between paragraphs.</p>
            <textarea data-testid="edit-teaching_content" value={f.teaching_content || ""} onChange={(e) => set("teaching_content", e.target.value)} rows={8}
              className="mt-1 w-full rounded-lg border border-subtle bg-app px-3 py-2 text-sm text-ink outline-none focus:border-[var(--brand)] resize-none" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink">Key takeaways (one per line)</label>
            <textarea data-testid="edit-key_takeaways" value={f.key_takeaways || ""} onChange={(e) => set("key_takeaways", e.target.value)} rows={4}
              className="mt-1 w-full rounded-lg border border-subtle bg-app px-3 py-2 text-sm text-ink outline-none focus:border-[var(--brand)] resize-none" />
          </div>
          <I label="Learn cards, one per line, format: Title :: Body" k="learn_content" rows={6} />
          <I label="Today's action" k="action" rows={2} />
          <I label="Reflection question" k="reflect" rows={2} />
          <I label="Complete button label" k="button_label" />
          <div>
            <label className="text-xs font-semibold text-ink">Interactive elements (advanced · JSON)</label>
            <p className="text-[11px] text-ink-muted mb-1">A list of fields. Types: score, select, multiselect, checklist, text, textlist, number, time, boolean, budget, meal, meal_list. Edit labels/options here, saved to the database, no rebuild needed.</p>
            <textarea data-testid="edit-interactive_json" value={f.interactive_json || ""} onChange={(e) => set("interactive_json", e.target.value)} rows={8}
              className="mt-1 w-full rounded-lg border border-subtle bg-app px-3 py-2 text-xs font-mono text-ink outline-none focus:border-[var(--brand)] resize-none" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink">Rich learn blocks (advanced · JSON)</label>
            <p className="text-[11px] text-ink-muted mb-1">Optional structured Learn content. Block types: paragraph, phases, symptom_grid, strategies, callout, tips. When present, this replaces the plain learn cards on the lesson screen.</p>
            <textarea data-testid="edit-learn_blocks_json" value={f.learn_blocks_json || ""} onChange={(e) => set("learn_blocks_json", e.target.value)} rows={8}
              className="mt-1 w-full rounded-lg border border-subtle bg-app px-3 py-2 text-xs font-mono text-ink outline-none focus:border-[var(--brand)] resize-none" />
          </div>
          <button data-testid="editor-save" onClick={save} className="w-full bg-brand text-white font-semibold py-3 rounded-full mt-2">Save lesson</button>
        </div>
      </div>
    </div>
  );
}

function ToolkitEditor({ item, onClose, onSaved }) {
  const isNew = !item;
  const [f, setF] = useState(item ? {
    ...item, content: (item.content || []).join("\n"), priority_tags: (item.priority_tags || []).join(", "),
  } : { slug: "", title: "", category: "", description: "", content: "", tool: "", priority_tags: "", sort_order: 99, published: true });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [uploading, setUploading] = useState(false);
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, document_url: data.document_url, document_name: data.document_name }));
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const save = async () => {
    const payload = {
      slug: f.slug, title: f.title, category: f.category, description: f.description,
      content: f.content.split("\n").filter((x) => x.trim()),
      downloadable_file: f.downloadable_file || null,
      document_url: f.document_url || null,
      document_name: f.document_name || null,
      tool: f.tool || null,
      priority_tags: f.priority_tags.split(",").map((x) => x.trim()).filter(Boolean),
      sort_order: Number(f.sort_order || 99), published: f.published !== false,
    };
    try {
      if (isNew) await api.post("/admin/toolkit", payload);
      else await api.put(`/admin/toolkit/${item.id}`, payload);
      toast.success("Resource saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };
  const I = ({ label, k, rows }) => (
    <div>
      <label className="text-xs font-semibold text-ink">{label}</label>
      {rows ? (
        <textarea data-testid={`tk-${k}`} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} rows={rows}
          className="mt-1 w-full rounded-lg border border-subtle bg-app px-3 py-2 text-sm text-ink outline-none focus:border-[var(--brand)] resize-none" />
      ) : (
        <input data-testid={`tk-${k}`} value={f[k] || ""} onChange={(e) => set(k, e.target.value)}
          className="mt-1 w-full rounded-lg border border-subtle bg-app px-3 py-2 text-sm text-ink outline-none focus:border-[var(--brand)]" />
      )}
    </div>
  );
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="toolkit-editor">
      <div className="bg-app w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-app px-5 py-4 flex items-center justify-between border-b border-subtle">
          <div className="font-serif text-lg font-bold text-ink">{isNew ? "New resource" : "Edit resource"}</div>
          <button onClick={onClose}><X size={22} className="text-ink-muted" /></button>
        </div>
        <div className="p-5 space-y-3">
          {isNew && <I label="Slug (unique id)" k="slug" />}
          <I label="Title" k="title" />
          <I label="Category" k="category" />
          <I label="Description" k="description" rows={2} />
          <I label="Content (one point per line)" k="content" rows={5} />
          <div className="grid grid-cols-2 gap-3">
            <I label="Tool (breathing-timer/planner/workbook)" k="tool" />
            <I label="Sort order" k="sort_order" />
          </div>
          <I label="Priority tags (comma separated)" k="priority_tags" />

          <div className="rounded-xl border border-subtle bg-white p-4">
            <label className="text-xs font-semibold text-ink">Downloadable document (PDF, DOC, DOCX, PNG, JPG)</label>
            {f.document_url ? (
              <div className="mt-2 flex items-center justify-between gap-2 bg-sage rounded-lg px-3 py-2">
                <span className="text-sm text-ink truncate" data-testid="tk-doc-name">{f.document_name || "Attached file"}</span>
                <button type="button" data-testid="tk-doc-remove"
                  onClick={() => setF((p) => ({ ...p, document_url: null, document_name: null }))}
                  className="text-xs font-semibold text-[var(--accent-c)]">Remove</button>
              </div>
            ) : (
              <label className="mt-2 flex items-center justify-center gap-2 border-2 border-dashed border-subtle rounded-lg py-3 cursor-pointer text-sm text-ink-soft hover:border-[var(--brand)]" data-testid="tk-doc-upload-label">
                <Upload size={16} />
                {uploading ? "Uploading…" : "Choose a file to upload"}
                <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={uploadDoc} disabled={uploading} className="hidden" data-testid="tk-doc-input" />
              </label>
            )}
            <p className="text-[11px] text-ink-muted mt-2">Customers will see a Download button on this resource. Max 20MB.</p>
          </div>

          <button data-testid="tk-save" onClick={save} className="w-full bg-brand text-white font-semibold py-3 rounded-full mt-2">Save resource</button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState(null);
  const [toolkit, setToolkit] = useState([]);
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [trials, setTrials] = useState(null);
  const [submissions, setSubmissions] = useState(null);
  const [approving, setApproving] = useState(null);
  const [editLesson, setEditLesson] = useState(null);
  const [editTool, setEditTool] = useState(undefined); // undefined=closed, null=new, obj=edit

  const loadContent = () => api.get("/admin/content").then((r) => setContent(r.data)).catch(() => {});
  const loadToolkit = () => api.get("/admin/toolkit").then((r) => setToolkit(r.data.items)).catch(() => {});
  const loadSubmissions = () => api.get("/admin/submissions").then((r) => setSubmissions(r.data)).catch(() => {});
  const approveSubmission = async (id) => {
    setApproving(id);
    try {
      const res = await api.post(`/admin/submissions/${id}/approve`);
      if (res.data.status === "sent") {
        toast.success("Submission sent to HQ");
      } else {
        const r = res.data.hq_response;
        const msg = typeof r === "string" ? r : (r?.body || r?.error || `HTTP ${r?.status_code}`);
        toast.error(`HQ rejected the submission: ${msg}`);
      }
      loadSubmissions();
    } catch (e) {
      toast.error(formatApiError(e) || "Could not reach HQ");
      loadSubmissions();
    } finally {
      setApproving(null);
    }
  };

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/home");
      return;
    }
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    loadContent();
    loadToolkit();
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/admin/trials").then((r) => setTrials(r.data)).catch(() => {});
    api.get("/admin/submissions").then((r) => setSubmissions(r.data)).catch(() => {});
  }, [user, navigate]);

  const doLogout = async () => { await logout(); navigate("/"); };

  const togglePublish = async (day) => {
    await api.patch(`/admin/lessons/${day}/publish`);
    loadContent();
  };
  const deleteTool = async (id) => {
    await api.delete(`/admin/toolkit/${id}`);
    loadToolkit();
  };

  const statCards = stats ? [
    { icon: Users, label: "Customers", value: stats.total_customers },
    { icon: CheckCircle2, label: "With access", value: stats.with_access },
    { icon: PoundSterling, label: "Revenue", value: `£${stats.revenue}` },
    { icon: TrendingUp, label: "Day completions", value: stats.total_day_completions },
  ] : [];

  return (
    <div className="min-h-screen bg-app" data-testid="admin-page">
      <header className="sticky top-0 z-30 backdrop-blur-md border-b border-subtle" style={{ backgroundColor: "rgba(250,248,245,0.9)" }}>
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg font-bold text-ink">Admin</div>
            <div className="text-ink-muted text-xs">The 30 Day Menopause Reset</div>
          </div>
          <button onClick={doLogout} data-testid="admin-logout" className="text-ink-soft flex items-center gap-1.5 text-sm font-semibold">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-white rounded-2xl p-5 card-shadow" data-testid={`admin-stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}>
                <Icon size={18} className="text-brand" />
                <div className="font-serif text-3xl font-bold text-ink mt-2">{s.value}</div>
                <div className="text-ink-muted text-xs">{s.label}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-1.5 bg-app-secondary rounded-full p-1 w-fit overflow-x-auto max-w-full">
          {TABS.map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-full text-sm font-semibold capitalize whitespace-nowrap transition-colors ${tab === t ? "bg-white text-brand card-shadow" : "text-ink-muted"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <div className="mt-5 bg-white rounded-2xl card-shadow overflow-hidden" data-testid="admin-users-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-muted border-b border-subtle">
                    <th className="px-5 py-3 font-semibold">Name</th>
                    <th className="px-5 py-3 font-semibold">Email</th>
                    <th className="px-5 py-3 font-semibold">Onboarded</th>
                    <th className="px-5 py-3 font-semibold">Access</th>
                    <th className="px-5 py-3 font-semibold">Day</th>
                    <th className="px-5 py-3 font-semibold">Done</th>
                    <th className="px-5 py-3 font-semibold">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-subtle last:border-0">
                      <td className="px-5 py-3 text-ink font-medium">{u.first_name || "—"}</td>
                      <td className="px-5 py-3 text-ink-soft">{u.email}</td>
                      <td className="px-5 py-3">{u.onboarding_complete ? "✓" : "—"}</td>
                      <td className="px-5 py-3">{u.has_access ? "✓" : "—"}</td>
                      <td className="px-5 py-3 text-ink-soft">{u.current_day}</td>
                      <td className="px-5 py-3 text-ink-soft">{u.completed_count}/30</td>
                      <td className="px-5 py-3 text-ink-soft">{u.last_active ? new Date(u.last_active).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-ink-muted">No customers yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "trials" && (
          <div className="mt-5 space-y-4" data-testid="admin-trials">
            {!trials ? (
              <div className="text-ink-muted text-sm">Loading trial data…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-white rounded-2xl p-5 card-shadow" data-testid="trials-started">
                    <Sparkles size={18} className="text-brand" />
                    <div className="font-serif text-3xl font-bold text-ink mt-2">{trials.trials_started}</div>
                    <div className="text-ink-muted text-xs">Trials started</div>
                  </div>
                  <div className="bg-white rounded-2xl p-5 card-shadow" data-testid="trials-converted">
                    <CheckCircle2 size={18} className="text-brand" />
                    <div className="font-serif text-3xl font-bold text-ink mt-2">{trials.converted}</div>
                    <div className="text-ink-muted text-xs">Converted to paid</div>
                  </div>
                  <div className="bg-white rounded-2xl p-5 card-shadow" data-testid="trials-conversion-rate">
                    <TrendingUp size={18} className="text-brand" />
                    <div className="font-serif text-3xl font-bold text-ink mt-2">{trials.conversion_rate}%</div>
                    <div className="text-ink-muted text-xs">Conversion rate</div>
                  </div>
                  <div className="bg-white rounded-2xl p-5 card-shadow" data-testid="trials-active">
                    <Hourglass size={18} className="text-brand" />
                    <div className="font-serif text-3xl font-bold text-ink mt-2">{trials.active_trials}</div>
                    <div className="text-ink-muted text-xs">Active trials</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-2xl p-5 card-shadow">
                    <div className="text-ink-muted text-xs">Expired without subscribing</div>
                    <div className="font-serif text-2xl font-bold text-ink mt-1" data-testid="trials-expired">{trials.expired_trials}</div>
                  </div>
                  <div className="bg-white rounded-2xl p-5 card-shadow">
                    <div className="text-ink-muted text-xs">Win-back recoveries</div>
                    <div className="font-serif text-2xl font-bold text-ink mt-1" data-testid="trials-winback-recoveries">
                      {trials.winback_recoveries}
                      <span className="text-ink-muted text-sm font-medium"> of {trials.winback_sent} sent</span>
                    </div>
                    <div className="text-[11px] text-accent-c font-semibold mt-1">{trials.winback_rate}% came back after the win-back email</div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-5 card-shadow" data-testid="trials-emails">
                  <div className="flex items-center gap-2 mb-3">
                    <Mail size={16} className="text-brand" />
                    <div className="font-semibold text-ink">Trial emails sent</div>
                  </div>
                  <div className="space-y-2">
                    {trials.emails.map((e) => (
                      <div key={e.key} className="flex items-center justify-between text-sm" data-testid={`trials-email-${e.key}`}>
                        <span className="text-ink-soft">{e.label}</span>
                        <span className="font-semibold text-ink">{e.sent}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-ink-muted mt-3">Counts reflect emails sent by the app. Open tracking is not enabled.</p>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "submissions" && (
          <div className="mt-5 space-y-3" data-testid="admin-submissions">
            {submissions && !submissions.hq_configured && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm" data-testid="hq-not-configured">
                HQ intake isn't fully configured (missing URL or key). Approving will fail until it's set.
              </div>
            )}
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl font-bold text-ink flex items-center gap-2"><Inbox size={18} className="text-brand" /> Recent submissions</h3>
              <button data-testid="submissions-refresh" onClick={loadSubmissions} className="text-xs font-semibold text-ink-muted hover:text-brand">Refresh</button>
            </div>
            <div className="bg-white rounded-2xl card-shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-ink-muted border-b border-subtle">
                      <th className="px-5 py-3 font-semibold">Name</th>
                      <th className="px-5 py-3 font-semibold">Email</th>
                      <th className="px-5 py-3 font-semibold">Type</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3 font-semibold">Received</th>
                      <th className="px-5 py-3 font-semibold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(submissions?.submissions || []).map((s) => (
                      <tr key={s.id} className="border-b border-subtle last:border-0" data-testid={`submission-row-${s.id}`}>
                        <td className="px-5 py-3 text-ink font-medium">{s.name}</td>
                        <td className="px-5 py-3 text-ink-soft">{s.email}</td>
                        <td className="px-5 py-3"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sage text-brand capitalize">{s.kind}</span></td>
                        <td className="px-5 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${s.status === "sent" ? "bg-green-100 text-green-700" : s.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`submission-status-${s.id}`}>{s.status}</span>
                          {s.status === "failed" && s.hq_response && (
                            <div className="text-[11px] text-red-500 mt-1 max-w-[220px] truncate" title={typeof s.hq_response === "string" ? s.hq_response : JSON.stringify(s.hq_response)}>
                              {typeof s.hq_response === "string" ? s.hq_response : (s.hq_response.body || s.hq_response.error || `HTTP ${s.hq_response.status_code}`)}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-3 text-ink-soft">{s.created_at ? new Date(s.created_at).toLocaleString() : "—"}</td>
                        <td className="px-5 py-3 text-right">
                          {s.status === "sent" ? (
                            <span className="text-xs text-green-600 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={14} /> Sent</span>
                          ) : (
                            <button
                              data-testid={`approve-live-${s.id}`}
                              disabled={approving === s.id}
                              onClick={() => approveSubmission(s.id)}
                              className="inline-flex items-center gap-1.5 bg-brand text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-90 disabled:opacity-50 transition-opacity">
                              <Send size={13} /> {approving === s.id ? "Sending…" : s.status === "failed" ? "Retry" : "Approve live"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {submissions && submissions.submissions.length === 0 && (
                      <tr><td colSpan={6} className="px-5 py-8 text-center text-ink-muted">No submissions yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "content" && content && (
          <div className="mt-5 space-y-3" data-testid="admin-content">
            {content.lessons.map((l) => (
              <div key={l.day} className="bg-white rounded-2xl p-4 card-shadow flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${l.published ? "bg-sage text-brand" : "bg-app-secondary text-ink-muted"}`}>{l.day}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-accent-c text-xs font-semibold">{l.week_title} · {l.category}</div>
                  <div className="font-semibold text-ink text-sm truncate">{l.title}</div>
                </div>
                <button data-testid={`publish-${l.day}`} onClick={() => togglePublish(l.day)} className="text-ink-muted p-2" title={l.published ? "Unpublish" : "Publish"}>
                  {l.published ? <Eye size={17} /> : <EyeOff size={17} />}
                </button>
                <button data-testid={`edit-lesson-${l.day}`} onClick={() => setEditLesson(l)} className="text-brand p-2"><Pencil size={17} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "toolkit" && (
          <div className="mt-5" data-testid="admin-toolkit">
            <button data-testid="add-toolkit" onClick={() => setEditTool(null)} className="mb-3 bg-brand text-white font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 text-sm">
              <Plus size={16} /> Add resource
            </button>
            <div className="space-y-3">
              {toolkit.map((t) => (
                <div key={t.id} className="bg-white rounded-2xl p-4 card-shadow flex items-center gap-3">
                  <BookOpen size={18} className="text-brand flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-accent-c text-xs font-semibold">{t.category}</div>
                    <div className="font-semibold text-ink text-sm truncate">{t.title}</div>
                  </div>
                  <button data-testid={`edit-tool-${t.slug}`} onClick={() => setEditTool(t)} className="text-brand p-2"><Pencil size={17} /></button>
                  <button data-testid={`delete-tool-${t.slug}`} onClick={() => deleteTool(t.id)} className="text-red-400 p-2"><Trash2 size={17} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "payments" && (
          <div className="mt-5 bg-white rounded-2xl card-shadow overflow-hidden" data-testid="admin-payments">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-muted border-b border-subtle">
                    <th className="px-5 py-3 font-semibold">Email</th>
                    <th className="px-5 py-3 font-semibold">Amount</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-subtle last:border-0">
                      <td className="px-5 py-3 text-ink-soft">{p.email}</td>
                      <td className="px-5 py-3 text-ink font-medium">£{p.amount} {(p.currency || "").toUpperCase()}</td>
                      <td className="px-5 py-3"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sage text-brand">{p.status}</span></td>
                      <td className="px-5 py-3 text-ink-soft">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                  {payments.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-ink-muted">No payments yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "analytics" && analytics && (
          <div className="mt-5 space-y-4" data-testid="admin-analytics">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl p-4 card-shadow"><div className="font-serif text-2xl font-bold text-ink">{analytics.total_customers}</div><div className="text-ink-muted text-xs">Customers</div></div>
              <div className="bg-white rounded-2xl p-4 card-shadow"><div className="font-serif text-2xl font-bold text-ink">{analytics.active_customers}</div><div className="text-ink-muted text-xs">Active (7d)</div></div>
              <div className="bg-white rounded-2xl p-4 card-shadow"><div className="font-serif text-2xl font-bold text-ink">{analytics.avg_days_completed}</div><div className="text-ink-muted text-xs">Avg days done</div></div>
            </div>
            <div className="bg-white rounded-2xl p-5 card-shadow">
              <div className="font-semibold text-ink mb-3">Completion by day</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.completion_by_day} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD5" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#88988E" }} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#88988E" }} tickLine={false} axisLine={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B594C" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-2xl p-5 card-shadow">
              <div className="font-semibold text-ink mb-3">Drop-off by day</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.dropoff_by_day} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD5" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#88988E" }} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#88988E" }} tickLine={false} axisLine={false} />
                  <Tooltip />
                  <Bar dataKey="dropoff" fill="#C87D65" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-2xl p-5 card-shadow">
              <div className="font-semibold text-ink mb-3">Toolkit usage</div>
              {analytics.toolkit_usage.length === 0 ? (
                <div className="text-ink-muted text-sm">No toolkit usage recorded yet.</div>
              ) : (
                <div className="space-y-2">
                  {analytics.toolkit_usage.map((u) => (
                    <div key={u.slug} className="flex items-center justify-between text-sm">
                      <span className="text-ink-soft">{u.slug}</span>
                      <span className="font-semibold text-ink">{u.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {editLesson && <LessonEditor lesson={editLesson} onClose={() => setEditLesson(null)} onSaved={loadContent} />}
      {editTool !== undefined && <ToolkitEditor item={editTool} onClose={() => setEditTool(undefined)} onSaved={loadToolkit} />}
    </div>
  );
}
